/**
 * @author       yontaek@zumper.com (Yon Taek Chung)
 * @copyright    Copyright (c) 2023, Zumper
 * @description  PinMarkerContainer
 */

import { selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import { isWebGLError, trackGAEvent } from '@zcode/nr-utils'
import { hocCompose, withErrorBoundary } from '@zcode/react-utils'
import type { Listable, Pin, RawPinInventorySearch, State } from '@zcode/state-type'
import { connect } from 'react-redux'
import { ANALYTIC_CHANNEL, EVENT_OPENED, PIN_MARKER } from '../../../constants/marker'
import { getCheckVisited, getHoveredListable } from '../../../inspectors'
import { logger } from '../../../logger'
import { setVisitedEntity } from '../../../module/clusters/actions'
import { setHoveredClusterItem } from '../../../module/searchUi/actions'
import { trackPinClickEvent } from '../../../module/traktor/thunks/trackPinClickEvent'
import type { Dispatch } from '../../../module/types'
import type { MixpanelMaplistClickType } from '../../../types'
import { PinMarker, type PinMarkerProps } from './PinMarker'

const errorCallback = (error: Error, info: string | null | undefined, forwardProps: unknown) => {
  if (!isWebGLError(error)) {
    logger.error('ErrorBoundary: caught by PinMarkerContainer', info, forwardProps, error)
  }
}

interface OwnPropsType {
  analyticChannel?: string
  lat?: number
  listable: {
    pin: Listable | Pin<RawPinInventorySearch>
    showPriceMarker: boolean
  }
  lng?: number
  mixpanelMaplistClick: ({
    buttonClicked,
    entity,
    pageName,
    section,
  }: MixpanelMaplistClickType) => void
}

const mapStateToProps = (state: State, ownProps: OwnPropsType) => {
  const { listable } = ownProps
  const { buildingId, isSpotlight, listingId, priceTxt, uId } = listable.pin ?? {}
  const checkVisited = getCheckVisited(state)

  const hoveredEntity = getHoveredListable(state)

  let isHovered = false
  if (hoveredEntity) {
    if (hoveredEntity.buildingId) {
      isHovered = hoveredEntity.buildingId === buildingId
    } else if (hoveredEntity.listingId) {
      isHovered = hoveredEntity.listingId === listingId
    } else {
      isHovered = hoveredEntity.uId === uId
    }
  }

  const isVisited = checkVisited({ buildingId, listingId, uId })

  return {
    isDot: !listable.showPriceMarker && !isSpotlight,
    isHovered,
    isSpotlight,
    isVisited,
    isMobile: selectIsMobile(state),
    listable,
    priceTxt,
  } satisfies Partial<PinMarkerProps>
}

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnPropsType) => {
  const { analyticChannel, listable, mixpanelMaplistClick } = ownProps

  return {
    onClick: () => {
      if (!listable) {
        return
      }
      const { buildingId, listingId, lat, lng } = listable.pin
      trackGAEvent(analyticChannel || ANALYTIC_CHANNEL, EVENT_OPENED)
      mixpanelMaplistClick({
        buttonClicked: 'Property Pin',
        entity: listable.pin,
      })
      dispatch(
        trackPinClickEvent({
          buildingId,
          listingId,
          lat,
          lng,
          markerType: PIN_MARKER,
        })
      )
      dispatch(setVisitedEntity(listable.pin))
    },
    onMouseOver: () => {
      dispatch(setHoveredClusterItem(listable))
    },
    onMouseOut: () => {
      dispatch(setHoveredClusterItem(null))
    },
  } satisfies Partial<PinMarkerProps>
}

export const PinMarkerContainer = hocCompose(
  connect(mapStateToProps, mapDispatchToProps),
  withErrorBoundary({ displayName: 'PinMarkerContainer', errorCallback }),
  PinMarker
)
